import React, { useCallback, useState } from 'react';
import styles from './index.module.scss';
import { challengeComponent } from './content';
import HeadContainer from '../../head-container';
import classNames from 'classnames';
import { getImageFromS3 } from '../../../imageService';

const MotivationalScreensaverChallenge = ({
  setScreenNumber,
  challengeId,
  afterResultButtonScreen
}) => {
  const challengeList = challengeComponent.find(
    (item) => item?.id === challengeId
  );
  const [challengeStep, setChallengeStep] = useState(0);
  const [selectQuestion, setSelectQuestion] = useState(
    challengeList?.questions[0]
  );
  const [correctAnswerValue, setCorrectAnswerValue] = useState(null);
  const [isWelcomeScreen, setIsWelcomeScreen] = useState(true);
  const [isResultScreen, setIsResultScreen] = useState(false);
  const [isAfterResultScreen, setIsAfterResultScreen] = useState(false);
  const [answers, setAnswers] = useState([]);

  const onAnswerClick = useCallback(
    (value) => {
      setAnswers((prev) => ({ ...prev, [challengeStep]: value }));
    },
    [challengeStep]
  );

  const onFinishClick = () => {
    const answerCounts = {};
    for (const key in answers) {
      const answerNumber = parseInt(
        answers[key].answerId.toString().split('.')[1]
      );
      answerCounts[answerNumber] = (answerCounts[answerNumber] || 0) + 1;
    }

    let mostFrequentAnswer = null;
    let maxCount = 0;
    for (const answer in answerCounts) {
      if (answerCounts[answer] > maxCount) {
        maxCount = answerCounts[answer];
        mostFrequentAnswer = parseInt(answer);
      }
    }

    setCorrectAnswerValue(
      challengeList?.resultList?.find((item) => item?.id === mostFrequentAnswer)
    );
    setIsResultScreen(true);
  };

  const handleDownload = async () => {
    const imageUrl = correctAnswerValue?.screensaver;
    if (!imageUrl) {
      return;
    }

    const fileName = `motivational-screensaver-${correctAnswerValue?.id}.png`;

    try {
      // The screensaver lives on S3 (cross-origin), so the `download`
      // attribute alone is ignored — fetch it as a blob to force a download.
      const response = await fetch(imageUrl);
      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(objectUrl);
    } catch (e) {
      // Fallback (e.g. CORS-blocked fetch): open the image in a new tab.
      window.open(imageUrl, '_blank');
    }
  };

  const resetChallenge = () => {
    setChallengeStep(0);
    setSelectQuestion(challengeList?.questions[0]);
    setCorrectAnswerValue(null);
    setIsWelcomeScreen(true);
    setIsResultScreen(false);
    setIsAfterResultScreen(false);
    setAnswers([]);
  };

  return (
    challengeList && (
      <div
        className={styles.wrapper}
        style={{
          backgroundImage: `url(${getImageFromS3('/images/motivational-screensaver/bg.png')})`
        }}
      >
        <div style={{ padding: '20px 30px 0' }}>
          <HeadContainer setScreenNumber={setScreenNumber} />
        </div>
        {isWelcomeScreen && !isResultScreen && (
          <div className={styles.welcomeContainer}>
            <p className={styles.title}>{challengeList?.title}</p>
            <p className={styles.description}>{challengeList?.description}</p>
            <p className={styles.subDescription}>
              {challengeList?.subDescription}
            </p>
            <button onClick={() => setIsWelcomeScreen(false)}>
              {challengeList?.startButtonTitle}
            </button>
          </div>
        )}
        {!isWelcomeScreen && !isResultScreen && !isAfterResultScreen && (
          <div className={styles.challengeContainer}>
            <div className={styles.stepToolbar}>
              {challengeList.questions.map((item, index) => (
                <div
                  key={index}
                  className={classNames(
                    styles.stepItem,
                    index === challengeStep && styles.activeStepItem
                  )}
                />
              ))}
            </div>
            <div className={styles.questionContainer}>
              <p className={styles.title}>{selectQuestion?.title}</p>
              {selectQuestion?.image ? (
                <img
                  src={selectQuestion?.image}
                  alt="question"
                  className={styles.questionImage}
                />
              ) : (
                <div className={styles.imagePlaceholder} />
              )}
              <div className={styles.answerList}>
                {selectQuestion &&
                  selectQuestion?.answerList?.map((item) => (
                    <div
                      key={item?.id}
                      className={classNames(
                        styles.answerItem,
                        item?.id === answers[challengeStep]?.answerId &&
                          styles.selectedAnswerItem
                      )}
                      onClick={() =>
                        onAnswerClick({
                          answerId: item?.id,
                          answer: item?.title
                        })
                      }
                    >
                      <p>{item?.title}</p>
                    </div>
                  ))}
              </div>
            </div>
            <div className={styles.buttonContainer}>
              <div
                className={styles.leftButton}
                onClick={() => {
                  if (challengeStep > 0) {
                    setChallengeStep(challengeStep - 1);
                    setSelectQuestion(
                      challengeList?.questions[challengeStep - 1]
                    );
                  } else {
                    resetChallenge();
                  }
                }}
              >
                <img
                  src="/images/luxury-expert-challenge/arrow.svg"
                  alt="arrow"
                />
                <span>Назад</span>
              </div>
              {answers[challengeStep]?.answerId && (
                <div
                  className={styles.rightButton}
                  onClick={() => {
                    if (challengeStep + 1 >= challengeList?.questions?.length) {
                      onFinishClick();
                      return;
                    }
                    setChallengeStep(challengeStep + 1);
                    setSelectQuestion(
                      challengeList?.questions[challengeStep + 1]
                    );
                  }}
                >
                  <span>Далі</span>
                  <img
                    src="/images/luxury-expert-challenge/arrow.svg"
                    alt="arrow"
                  />
                </div>
              )}
            </div>
          </div>
        )}
        {isResultScreen && !isWelcomeScreen && correctAnswerValue && (
          <div className={styles.resultContainer}>
            <p className={styles.headResultTitle}>Результат</p>
            {correctAnswerValue?.image ? (
              <img
                src={correctAnswerValue?.image}
                alt="result"
                className={styles.resultImage}
              />
            ) : (
              <div className={styles.resultImagePlaceholder} />
            )}
            <p className={styles.description}>
              {correctAnswerValue?.description}
            </p>
            <div className={styles.downloadButtonContainer}>
              <button onClick={handleDownload}>Завантажити заставку</button>
            </div>
            <div className={styles.buttonContainer}>
              <div
                className={styles.leftButton}
                onClick={() => {
                  setIsResultScreen(false);
                }}
              >
                <img
                  src="/images/luxury-expert-challenge/arrow.svg"
                  alt="arrow"
                />
                <span>Назад</span>
              </div>
              <div
                className={styles.rightButton}
                onClick={() => {
                  setIsResultScreen(false);
                  setIsAfterResultScreen(true);
                }}
              >
                <span>Далі</span>
                <img
                  src="/images/luxury-expert-challenge/arrow.svg"
                  alt="arrow"
                />
              </div>
            </div>
          </div>
        )}
        {isAfterResultScreen && !isResultScreen && !isWelcomeScreen && (
          <div className={styles.afterResultContainer}>
            <p className={styles.title}>{challengeList?.afterResultTitle}</p>
            <p className={styles.description}>
              {challengeList?.afterResultDescription}
            </p>
            <button
              onClick={() => {
                resetChallenge();
                afterResultButtonScreen();
              }}
            >
              Далі
            </button>
          </div>
        )}
      </div>
    )
  );
};

export default MotivationalScreensaverChallenge;
